import type { PatternFormFieldItem } from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Nullable, RecordKey } from '@ricoh/ria.client.core/types/core/data.type';

import type { PatternValueSystemValueDocument } from '~/common/model/api/batch-type';
import type { PatternDocument } from '~/common/model/api/batch-type/pattern';

import { TRANSFORMATION_TEXT } from '~/common/constants/feature/settings/batch-type-library';

import type { PatternDocumentPathMap } from './_document.type';

import { DOCUMENT_PATH } from './_path';

const documentPathMap = Object.entries<PatternValueSystemValueDocument, string>(DOCUMENT_PATH).reduce(
  (prev, [pathKey, path]) => {
    prev.fromSystemValue[pathKey] = path;
    prev.toSystemValue[path] = pathKey;

    return prev;
  },
  {
    fromSystemValue: {},
    toSystemValue: {},
  } as PatternDocumentPathMap,
);

/** PatternDocument Value. */
export const patternDocumentValue = {
  documentField: {
    toPath: (name: string): string => `fields['${name}'].value`,
    fromPath: (path: string): string | undefined => {
      const result = /^fields\['(.+?)'\]\.value$/.exec(path);
      if (!result) {
        return undefined;
      }

      return result[1];
    },
  },

  documentVariable: {
    toPath: (name: string): string => `variables['${name}'].value`,
    fromPath: (path: string): string | undefined => {
      const result = /^variables\['(.+?)'\]\.value$/.exec(path);
      if (!result) {
        return undefined;
      }

      return result[1];
    },
  },

  documentBusinessRule: {
    toPath: (name: string): string => `rules['${name}']`,
    fromPath: (path: string): string | undefined => {
      const result = /^rules\['(.+?)'\]$/.exec(path);
      if (!result) {
        return undefined;
      }

      return result[1];
    },
  },
};

/** Create PatternDocument. */
export const createPatternDocument = {
  documentPath: (value: string, transformationText: boolean): Nullable<PatternDocument> => {
    const path = documentPathMap.fromSystemValue[value];
    if (!path) {
      return undefined;
    }

    return {
      $is: 'document',
      path,
      transformations: transformationText ? [TRANSFORMATION_TEXT] : undefined,
    };
  },

  documentField: (fieldName: string): PatternDocument => ({
    $is: 'document',
    path: patternDocumentValue.documentField.toPath(fieldName),
  }),

  documentVariable: (variableName: string): PatternDocument => ({
    $is: 'document',
    path: patternDocumentValue.documentVariable.toPath(variableName),
  }),

  documentBusinessRule: (ruleName: string): PatternDocument => ({
    $is: 'document',
    path: patternDocumentValue.documentBusinessRule.toPath(ruleName),
  }),
};

/** Create PatternDocument FormField. */
export const createPatternDocumentFormField = {
  documentPath: <TTabKey extends RecordKey>(tabKey: TTabKey, pattern: PatternDocument): Nullable<PatternFormFieldItem<TTabKey>> => {
    const value = documentPathMap.toSystemValue[pattern.path];

    return value
      ? {
          tabKey,
          value,
        }
      : undefined;
  },

  documentField: <TTabKey extends RecordKey>(tabKey: TTabKey, pattern: PatternDocument): Nullable<PatternFormFieldItem<TTabKey>> => {
    const value = patternDocumentValue.documentField.fromPath(pattern.path);

    return value
      ? {
          tabKey,
          value,
        }
      : undefined;
  },

  documentVariable: <TTabKey extends RecordKey>(tabKey: TTabKey, pattern: PatternDocument): Nullable<PatternFormFieldItem<TTabKey>> => {
    const value = patternDocumentValue.documentVariable.fromPath(pattern.path);

    return value
      ? {
          tabKey,
          value,
        }
      : undefined;
  },

  documentBusinessRule: <TTabKey extends RecordKey>(tabKey: TTabKey, pattern: PatternDocument): Nullable<PatternFormFieldItem<TTabKey>> => {
    const value = patternDocumentValue.documentBusinessRule.fromPath(pattern.path);

    return value
      ? {
          tabKey,
          value,
        }
      : undefined;
  },
};
